import { Button, List, Table, Tag, Typography } from 'antd';
import { ExperimentOutlined, FilePdfOutlined, FileSearchOutlined, FundOutlined, PictureOutlined } from '@ant-design/icons';
import PhaseShell, { SectionCard, StatChip } from '../../../components/registry/PhaseShell';
import type { PhasePageProps } from '../../../components/registry/RegistryPhaseWorkspace';
import {
  SectionEmpty,
  groupBySourceDocument,
  openSourcePdf,
  registryDate as date,
  renderDocGroups,
} from '../../../components/registry/registryDocGroups';
import { detailsText } from './detailsText';
import { imagingDocuments, isAfpLab } from './phases';

const { Text } = Typography;

interface AfpRow {
  collectedAt: string | null;
  value: string;
  unit: string | null;
  result: string | null;
  sourceDocumentId?: string | null;
}

/** Faza 2 — Screening & Diagnostic: AFP, imagistică, histopatologie, analize. */
export default function Faza2Page({ record, status }: PhasePageProps) {
  // AFP: tabela dedicată; dacă e goală, analizele AFP din labs[] (fără dublare în lista generală)
  const afpFromLabs = (record.afp?.length ?? 0) === 0;
  const afpRows: AfpRow[] = afpFromLabs
    ? (record.labs ?? [])
        .filter(isAfpLab)
        .map((l) => ({ collectedAt: l.collectedAt, value: l.value, unit: l.unit, result: l.flag, sourceDocumentId: l.sourceDocumentId }))
    : (record.afp ?? []).map((a) => ({
        collectedAt: a.collectedAt,
        value: String(a.value),
        unit: a.unit,
        result: a.result,
        sourceDocumentId: a.sourceDocumentId,
      }));
  const otherLabs = (record.labs ?? []).filter((l) => !afpFromLabs || !isAfpLab(l));
  const imaging = imagingDocuments(record);
  const histopathologies = record.histopathologies ?? [];

  return (
    <PhaseShell
      title="Faza 2 — Screening & Diagnostic"
      description="Screeningul și confirmarea diagnosticului (analize AFP, imagistică, histopatologie)."
      status={status}
      chips={
        <>
          <StatChip count={afpRows.length} label="valori AFP" />
          <StatChip count={imaging.length} label="documente imagistice" />
          <StatChip count={histopathologies.length} label="buletine HP" />
          <StatChip count={otherLabs.length} label="analize" />
        </>
      }
    >
      <SectionCard icon={<ExperimentOutlined />} title="AFP (alfa-fetoproteina)">
        {afpRows.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(afpRows, record.documents, (r) => r.sourceDocumentId),
            (rows) => (
              <Table<AfpRow>
                size="small"
                rowKey={(r) => `${r.collectedAt}-${r.value}`}
                pagination={false}
                dataSource={rows}
                columns={[
                  { title: 'Data', dataIndex: 'collectedAt', key: 'd', render: (v: string | null) => date(v) },
                  { title: 'Valoare', key: 'v', render: (_, r) => `${r.value}${r.unit ? ` ${r.unit}` : ''}` },
                  {
                    title: 'Rezultat',
                    dataIndex: 'result',
                    key: 'r',
                    render: (v: string | null) =>
                      v === 'ELEVATED' ? <Tag color="red">crescut</Tag> : v === 'H' ? <Tag color="red">crescut</Tag> : v ?? '—',
                  },
                ]}
              />
            ),
          )
        )}
      </SectionCard>

      <SectionCard icon={<PictureOutlined />} title="Imagistică (ecografie / elastografie / CEUS)">
        {imaging.length === 0 ? (
          <SectionEmpty />
        ) : (
          <List
            size="small"
            dataSource={imaging}
            renderItem={(d) => (
              <List.Item
                actions={[
                  <Button key="pdf" size="small" type="link" icon={<FilePdfOutlined />} onClick={() => openSourcePdf(d.fileKey)}>
                    Vezi PDF
                  </Button>,
                ]}
              >
                <Text>
                  {d.fileName} <Text type="secondary">· {date(d.documentDate)}</Text>
                </Text>
              </List.Item>
            )}
          />
        )}
      </SectionCard>

      <SectionCard icon={<FileSearchOutlined />} title="Histopatologie">
        {histopathologies.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(histopathologies, record.documents, (h) => h.sourceDocumentId),
            (items) => (
              <Table
                size="small"
                rowKey={(h) => `${h.bulletinNo}-${h.resultDate}`}
                pagination={false}
                dataSource={items}
                columns={[
                  { title: 'Buletin', dataIndex: 'bulletinNo', key: 'b', render: (v: string | null) => v ?? '—' },
                  { title: 'Data rezultatului', dataIndex: 'resultDate', key: 'd', render: (v: string | null) => date(v) },
                  { title: 'Tip tumoral', dataIndex: 'tumorType', key: 't' },
                  { title: 'Detalii', key: 'det', render: (_, h) => detailsText(h.details) || '—' },
                ]}
              />
            ),
          )
        )}
      </SectionCard>

      <SectionCard icon={<FundOutlined />} title="Analize de laborator">
        {otherLabs.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(otherLabs, record.documents, (l) => l.sourceDocumentId),
            (labs) => (
              <Table
                size="small"
                rowKey={(l) => `${l.collectedAt}-${l.testName}`}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                dataSource={labs}
                columns={[
                  { title: 'Data', dataIndex: 'collectedAt', key: 'd', render: (v: string | null) => date(v) },
                  { title: 'Analiză', dataIndex: 'testName', key: 'n' },
                  { title: 'Valoare', key: 'v', render: (_, l) => `${l.value}${l.unit ? ` ${l.unit}` : ''}${l.flag ? ` (${l.flag})` : ''}` },
                  { title: 'Interval', dataIndex: 'refRange', key: 'r', render: (v: string | null) => v ?? '—' },
                ]}
              />
            ),
          )
        )}
      </SectionCard>
    </PhaseShell>
  );
}
